import React from 'react';
import { Card, Button, Upload, Space, Tag, Progress, Typography, message } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { TaskDraftReport } from '../../services/tasks';

const { Text } = Typography;

interface User {
  id: number;
  name: string;
  email: string;
}

interface DraftTabProps {
  isEdit: boolean;
  draftReports: Array<TaskDraftReport | { id: string; task_id: number; attachment: null; enabled: boolean; created_at: string; creator?: User; file: File }>;
  uploadQueue: Array<{ file: File; id: string }>;
  uploadStatus: Record<string, 'pending' | 'uploading' | 'success' | 'error'>;
  uploadProgress: Record<string, number>;
  handleAddDraftReport: (file: File) => void;
  handleDeleteDraftReport: (reportId: number | string) => void;
  formatDateTime: (dateString?: string) => string;
}

export const DraftTab: React.FC<DraftTabProps> = ({
  isEdit,
  draftReports,
  uploadQueue,
  uploadStatus,
  uploadProgress,
  handleAddDraftReport,
  handleDeleteDraftReport,
  formatDateTime,
}) => {
  return (
    <Card
      title="Draft Reports"
      bodyStyle={{ padding: '16px' }}
      extra={
        isEdit ? (
          <Upload
            beforeUpload={(file) => {
              handleAddDraftReport(file);
              return false;
            }}
            showUploadList={false}
          >
            <Button type="link" icon={<PlusOutlined />}>Add</Button>
          </Upload>
        ) : (
          <span style={{ color: '#999' }}>Save task first</span>
        )
      }
    >
      {/* Upload Progress */}
      {uploadQueue.length > 0 && (
        <div style={{ marginBottom: 16, padding: '12px', background: '#fafafa', borderRadius: 4 }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Uploading {uploadQueue.length} file(s)</Text>
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {uploadQueue.map((item) => {
              const status = uploadStatus[item.id] || 'pending';
              const progress = uploadProgress[item.id] || 0;
              return (
                <div key={item.id} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text style={{ fontSize: '12px' }} ellipsis>
                      {item.file.name}
                    </Text>
                    <Tag
                      color={
                        status === 'success' ? 'green' :
                        status === 'error' ? 'red' :
                        status === 'uploading' ? 'blue' : 'default'
                      }
                    >
                      {status === 'success' ? 'Done' :
                       status === 'error' ? 'Failed' :
                       status === 'uploading' ? 'Uploading' : 'Pending'}
                    </Tag>
                  </div>
                  {status === 'uploading' && (
                    <Progress percent={progress} size="small" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {draftReports.map((report) => {
        const reportId: number | string = report.id;
        const isTemp = typeof reportId === 'string' && reportId.startsWith('temp-draft-');
        const fileName = isTemp 
          ? ('file' in report ? (report as any).file?.name : 'New file')
          : (report.attachment ? report.attachment.split('/').pop() : 'No attachment');
        
        const handleView = async () => {
          if (isTemp || !report.attachment) return;
          try {
            const response = await api.get(`/task-draft-reports/${report.id}/view`, {
              responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
          } catch (error: any) {
            message.error('Failed to view file');
          }
        };
        
        const handleDownload = async () => {
          if (isTemp || !report.attachment) return;
          try {
            const response = await api.get(`/task-draft-reports/${report.id}/download`, {
              responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName || 'draft-report';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
          } catch (error: any) {
            message.error('Failed to download file');
          }
        };
        
        return (
          <div key={report.id} style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>
              {isTemp ? (
                <Space>
                  <Text>{fileName}</Text>
                  <Tag color="orange">Pending</Tag>
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteDraftReport(report.id!)}
                    size="small"
                  />
                </Space>
              ) : report.attachment ? (
                <Space>
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={handleView}
                    size="small"
                  >
                    {fileName}
                  </Button>
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                    size="small"
                  />
                  {isEdit && (
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteDraftReport(report.id!)}
                      size="small"
                    />
                  )}
                </Space>
              ) : (
                <Text strong>No attachment</Text>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              By {report.creator?.name || 'Unknown'} {formatDateTime(report.created_at)}
            </div>
          </div>
        );
      })}
      {draftReports.length === 0 && (
        <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
          No draft reports
        </div>
      )}
    </Card>
  );
};

